import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  cleanupFirebaseRegistration,
  createFirebaseRegistrationSession,
  invalidateFirebaseMessagingInitialization,
  inspectFirebaseRegistrationHealth,
  resetFirebaseRegistrationForUnregisteredTarget,
} from "../services/firebaseMessagingClient";
import { invalidateRootServiceWorkerInitialization } from "../services/serviceWorkerRegistration";
import { getPushCapability } from "../services/pushCapability";
import { getOrCreatePushInstallation } from "../services/pushInstallation";
import {
  clearPushRegistrationCleanupIntent,
  clearPushRegistrationOwnerBinding,
  hasPushRegistrationOwnerBinding,
  hasPushRegistrationCleanupIntent,
  matchesPushRegistrationOwnerBinding,
  setPushRegistrationOwnerBinding,
  setPushRegistrationCleanupIntent,
} from "../services/pushRegistrationIntent";
import {
  claimPushRegistration,
  fetchPushRegistrations,
  isValidFirebaseInstallationId,
  revokePushRegistration,
} from "../services/pushRegistrationsApi";
import { getPushRegistrationConfigStatus } from "../services/pushRegistrationConfig";
import { buildPushRegistrationMetadata } from "../services/pushRegistrationMetadata";

export const REGISTRATION_WAIT_MS = 15000;
export const AUTOMATIC_REFRESH_MAX_ATTEMPTS = 2;

const EMPTY_ACTIONS = Object.freeze({
  registerCurrentDevice: async () => false,
  deactivateCurrentDevice: async () => false,
  prepareForLogout: async () => undefined,
  cleanupAfterAccountDeletion: async () => undefined,
  refresh: async () => false,
});

const DEFAULT_CONTEXT = Object.freeze({
  status: "registration_feature_disabled",
  active: false,
  registrationEnabled: false,
  vapidConfigured: false,
  currentDeviceReference: null,
  registrationConfigurationMismatch: false,
  actions: EMPTY_ACTIONS,
});

const PushRegistrationLifecycleContext = createContext(DEFAULT_CONTEXT);

function normalizeAccountKey(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function getPermissionState() {
  try {
    return getPushCapability().permissionState;
  } catch {
    return "unsupported";
  }
}

export function usePushRegistrationLifecycle() {
  return useContext(PushRegistrationLifecycleContext);
}

export default function PushRegistrationLifecycleProvider({
  accountKey = null,
  authResolved = false,
  lifecycleRef = null,
  children,
}) {
  const { i18n } = useTranslation();
  const config = getPushRegistrationConfigStatus();
  const [state, setState] = useState({
    status: config.enabled
      ? "registration_checking"
      : "registration_feature_disabled",
    active: false,
  });
  const mountedRef = useRef(true);
  const accountRef = useRef(null);
  const languageRef = useRef("en");
  const generationRef = useRef(0);
  const installationRef = useRef(null);
  const activeRef = useRef(false);
  const observerRef = useRef(null);
  const observerPromiseRef = useRef(null);
  const observerCreationQueueRef = useRef(Promise.resolve());
  const abortControllersRef = useRef(new Set());
  const loadAbortRef = useRef(null);
  const mutationQueueRef = useRef(Promise.resolve());
  const pendingFidsRef = useRef(new Set());
  const refreshRequiredRef = useRef(false);
  const providerTargetInvalidatedRef = useRef(false);
  const providerRecoveryPreparedRef = useRef(false);
  const automaticRefreshRef = useRef({ generation: 0, attempts: 0, promise: null });
  const registrationAttemptRef = useRef(null);
  const registrationAttemptSequenceRef = useRef(0);
  const registrationPromiseRef = useRef(null);
  const registerCurrentDeviceRef = useRef(null);
  const deactivationPromiseRef = useRef(null);
  const localCleanupPromiseRef = useRef(null);
  const localCleanupRetryRef = useRef(false);
  const automaticRegistrationRef = useRef(null);
  const pendingConsentRef = useRef(null);

  languageRef.current = i18n?.resolvedLanguage || i18n?.language || "en";

  const setLifecycleState = useCallback((next) => {
    if (mountedRef.current) setState(next);
  }, []);

  const operationIsCurrent = useCallback(
    (generation, owner, installationId = installationRef.current) =>
      generation === generationRef.current &&
      Boolean(owner) &&
      owner === accountRef.current &&
      Boolean(installationId) &&
      installationId === installationRef.current,
    []
  );

  const disposeObserver = useCallback(() => {
    observerPromiseRef.current = null;
    observerRef.current?.dispose?.();
    observerRef.current = null;
  }, []);

  const abortOwnedRequests = useCallback(() => {
    loadAbortRef.current?.abort();
    loadAbortRef.current = null;
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
  }, []);

  const finishRegistrationAttempt = useCallback((successful, attemptId = null) => {
    const attempt = registrationAttemptRef.current;
    if (!attempt || (attemptId !== null && attempt.id !== attemptId)) return;
    registrationAttemptRef.current = null;
    if (attempt.timeoutId) clearTimeout(attempt.timeoutId);
    attempt.resolve(successful);
  }, []);

  const abandonPreProviderInitialization = useCallback(() => {
    disposeObserver();
    observerCreationQueueRef.current = Promise.resolve();
    invalidateFirebaseMessagingInitialization();
    invalidateRootServiceWorkerInitialization();
  }, [disposeObserver]);

  const invalidateGeneration = useCallback(() => {
    const initializationWasPending = Boolean(
      observerPromiseRef.current ||
        registrationAttemptRef.current?.phase === "pre_provider"
    );
    generationRef.current += 1;
    if (initializationWasPending) abandonPreProviderInitialization();
    else disposeObserver();
    abortOwnedRequests();
    finishRegistrationAttempt(false);
    activeRef.current = false;
    refreshRequiredRef.current = false;
    providerTargetInvalidatedRef.current = false;
    providerRecoveryPreparedRef.current = false;
    automaticRefreshRef.current = {
      generation: generationRef.current,
      attempts: 0,
      promise: null,
    };
    automaticRegistrationRef.current = null;
    pendingConsentRef.current = null;
    pendingFidsRef.current.clear();
    mutationQueueRef.current = Promise.resolve();
    registrationPromiseRef.current = null;
    deactivationPromiseRef.current = null;
    return generationRef.current;
  }, [
    abandonPreProviderInitialization,
    abortOwnedRequests,
    disposeObserver,
    finishRegistrationAttempt,
  ]);

  const createTrackedController = useCallback(() => {
    const controller = new AbortController();
    abortControllersRef.current.add(controller);
    return controller;
  }, []);

  const releaseTrackedController = useCallback((controller) => {
    abortControllersRef.current.delete(controller);
  }, []);

  const enqueueMutation = useCallback((operation) => {
    const queued = mutationQueueRef.current
      .catch(() => undefined)
      .then(operation);
    mutationQueueRef.current = queued.catch(() => undefined);
    return queued;
  }, []);

  const cleanupLocalRegistration = useCallback(async ({ force = false } = {}) => {
    if (!force && !hasPushRegistrationCleanupIntent()) {
      localCleanupRetryRef.current = false;
      return true;
    }
    if (localCleanupPromiseRef.current) return localCleanupPromiseRef.current;

    const cleanup = Promise.resolve(cleanupFirebaseRegistration())
      .then((result) => {
        const successful = result?.status === "unregistered";
        localCleanupRetryRef.current = !successful;
        if (successful) clearPushRegistrationCleanupIntent();
        return successful;
      })
      .catch(() => {
        localCleanupRetryRef.current = true;
        return false;
      })
      .finally(() => {
        if (localCleanupPromiseRef.current === cleanup) {
          localCleanupPromiseRef.current = null;
        }
      });
    localCleanupPromiseRef.current = cleanup;
    return cleanup;
  }, []);

  const handleRegisteredRef = useRef(null);
  const handleUnregisteredRef = useRef(null);

  const ensureObserver = useCallback(
    async (
      generation,
      owner,
      installationId,
      { allowRegistration = false, permissionGranted = false } = {}
    ) => {
      const permissionIsGranted =
        getPermissionState() === "granted" || permissionGranted === true;
      if (
        !operationIsCurrent(generation, owner, installationId) ||
        !permissionIsGranted ||
        (!activeRef.current && !allowRegistration)
      ) {
        return null;
      }
      if (observerRef.current) return observerRef.current;
      if (observerPromiseRef.current?.generation === generation) {
        return observerPromiseRef.current.promise;
      }

      const creation = observerCreationQueueRef.current
        .catch(() => undefined)
        .then(() =>
          createFirebaseRegistrationSession({
            sessionKey: owner,
            onRegistered: (fid) =>
              handleRegisteredRef.current?.(
                fid,
                generation,
                owner,
                installationId
              ),
            onUnregistered: () =>
              handleUnregisteredRef.current?.(
                generation,
                owner,
                installationId
              ),
          })
        )
        .then((session) => {
          const creationStillCurrent =
            observerPromiseRef.current?.promise === creation;
          const registrationStillPending =
            registrationAttemptRef.current?.generation === generation;
          if (
            !creationStillCurrent ||
            session?.status !== "ready" ||
            !operationIsCurrent(generation, owner, installationId) ||
            (getPermissionState() !== "granted" && !permissionGranted) ||
            (!activeRef.current && !registrationStillPending)
          ) {
            session?.dispose?.();
            return null;
          }
          observerRef.current = session;
          return session;
        })
        .catch(() => null)
        .finally(() => {
          if (observerPromiseRef.current?.promise === creation) {
            observerPromiseRef.current = null;
          }
        });
      observerCreationQueueRef.current = creation.catch(() => undefined);
      observerPromiseRef.current = { generation, promise: creation };
      return creation;
    },
    [operationIsCurrent]
  );

  const handleRegistered = useCallback(
    (fid, generation, owner, installationId) => {
      const registrationAttempt = registrationAttemptRef.current;
      const explicitRegistrationPending =
        registrationAttempt?.generation === generation &&
        registrationAttempt?.owner === owner &&
        registrationAttempt?.installationId === installationId &&
        !registrationAttempt?.cancelled;
      const attemptId = explicitRegistrationPending
        ? registrationAttempt.id
        : null;
      const consent = explicitRegistrationPending
        ? registrationAttempt?.consent
        : undefined;
      if (
        !operationIsCurrent(generation, owner, installationId) ||
        (!activeRef.current && !explicitRegistrationPending)
      ) {
        return;
      }
      if (!isValidFirebaseInstallationId(fid)) {
        if (explicitRegistrationPending) {
          setLifecycleState({ status: "registration_failed", active: false });
          finishRegistrationAttempt(false);
          disposeObserver();
        }
        return;
      }
      if (pendingFidsRef.current.has(fid)) return;

      pendingFidsRef.current.add(fid);
      const wasActive = activeRef.current;
      enqueueMutation(async () => {
        if (!operationIsCurrent(generation, owner, installationId)) return false;
        if (
          attemptId !== null &&
          (registrationAttemptRef.current?.id !== attemptId ||
            registrationAttemptRef.current?.cancelled)
        ) {
          return false;
        }
        const controller = createTrackedController();
        if (attemptId !== null && registrationAttemptRef.current?.id === attemptId) {
          registrationAttemptRef.current.claimController = controller;
        }
        try {
          const claimOptions = { signal: controller.signal };
          if (consent !== undefined) claimOptions.consent = consent;
          const result = await claimPushRegistration(
            installationId,
            fid,
            buildPushRegistrationMetadata(getPushCapability(), languageRef.current),
            claimOptions
          );
          if (
            controller.signal.aborted ||
            !operationIsCurrent(generation, owner, installationId)
          ) {
            return false;
          }
          const active =
            result.registration.installationId === installationId &&
            result.registration.enabled;
          if (active) {
            setPushRegistrationCleanupIntent();
            await setPushRegistrationOwnerBinding(owner);
            if (
              consent !== undefined &&
              pendingConsentRef.current?.owner === owner &&
              pendingConsentRef.current?.installationId === installationId
            ) {
              pendingConsentRef.current = null;
            }
          }
          if (
            attemptId !== null &&
            (registrationAttemptRef.current?.id !== attemptId ||
              registrationAttemptRef.current?.cancelled)
          ) {
            return false;
          }
          activeRef.current = active;
          refreshRequiredRef.current = false;
          providerTargetInvalidatedRef.current = false;
          providerRecoveryPreparedRef.current = false;
          setLifecycleState({
            status: active ? "registration_active" : "registration_failed",
            active,
            currentDeviceReference: active
              ? result.registration.currentDeviceReference
              : null,
            registrationConfigurationMismatch: false,
          });
          finishRegistrationAttempt(active, attemptId);
          return active;
        } catch {
          if (
            !controller.signal.aborted &&
            operationIsCurrent(generation, owner, installationId) &&
            !wasActive
          ) {
            setLifecycleState({ status: "registration_failed", active: false });
            finishRegistrationAttempt(false);
          }
          return false;
        } finally {
          if (
            attemptId !== null &&
            registrationAttemptRef.current?.id === attemptId &&
            registrationAttemptRef.current.claimController === controller
          ) {
            registrationAttemptRef.current.claimController = null;
          }
          releaseTrackedController(controller);
          pendingFidsRef.current.delete(fid);
        }
      });
    },
    [
      createTrackedController,
      disposeObserver,
      enqueueMutation,
      finishRegistrationAttempt,
      operationIsCurrent,
      releaseTrackedController,
      setLifecycleState,
    ]
  );

  const handleUnregistered = useCallback(
    (generation, owner, installationId) => {
      if (
        !activeRef.current ||
        !operationIsCurrent(generation, owner, installationId)
      ) {
        return;
      }
      disposeObserver();
      clearPushRegistrationCleanupIntent();
      enqueueMutation(async () => {
        if (!operationIsCurrent(generation, owner, installationId)) return false;
        const controller = createTrackedController();
        try {
          await revokePushRegistration(installationId, {
            signal: controller.signal,
          });
          if (
            controller.signal.aborted ||
            !operationIsCurrent(generation, owner, installationId)
          ) {
            return false;
          }
          activeRef.current = false;
          setLifecycleState({
            status: config.vapidConfigured
              ? "registration_ready"
              : "registration_unavailable",
            active: false,
          });
          return true;
        } catch {
          if (
            !controller.signal.aborted &&
            operationIsCurrent(generation, owner, installationId)
          ) {
            setLifecycleState({ status: "deactivation_failed", active: true });
          }
          return false;
        } finally {
          releaseTrackedController(controller);
        }
      });
    },
    [
      config.vapidConfigured,
      createTrackedController,
      disposeObserver,
      enqueueMutation,
      operationIsCurrent,
      releaseTrackedController,
      setLifecycleState,
    ]
  );

  handleRegisteredRef.current = handleRegistered;
  handleUnregisteredRef.current = handleUnregistered;

  const refreshRegistrationProvenance = useCallback(
    (generation, owner, installationId) => {
      if (
        !refreshRequiredRef.current ||
        !activeRef.current ||
        getPermissionState() !== "granted" ||
        !operationIsCurrent(generation, owner, installationId)
      ) {
        return Promise.resolve(false);
      }
      if (automaticRefreshRef.current.generation !== generation) {
        automaticRefreshRef.current = { generation, attempts: 0, promise: null };
      }
      const refresh = automaticRefreshRef.current;
      if (refresh.promise) return refresh.promise;
      if (refresh.attempts >= AUTOMATIC_REFRESH_MAX_ATTEMPTS) {
        return Promise.resolve(false);
      }
      refresh.attempts += 1;
      const operation = (async () => {
        const session = await ensureObserver(generation, owner, installationId);
        if (
          session?.status !== "ready" ||
          !refreshRequiredRef.current ||
          !activeRef.current ||
          !operationIsCurrent(generation, owner, installationId)
        ) {
          return false;
        }
        try {
          await session.register();
          return true;
        } catch {
          return false;
        }
      })().finally(() => {
        if (automaticRefreshRef.current.promise === operation) {
          automaticRefreshRef.current.promise = null;
        }
      });
      refresh.promise = operation;
      return operation;
    },
    [ensureObserver, operationIsCurrent]
  );

  const reconcileCurrentDevice = useCallback(async () => {
    const generation = generationRef.current;
    const owner = accountRef.current;
    const installationId = installationRef.current;
    if (!config.enabled || !operationIsCurrent(generation, owner, installationId)) {
      return false;
    }

    loadAbortRef.current?.abort();
    const controller = createTrackedController();
    loadAbortRef.current = controller;
    setLifecycleState({
      status: "registration_checking",
      active: activeRef.current,
    });
    try {
      const result = await fetchPushRegistrations({ signal: controller.signal });
      if (
        controller.signal.aborted ||
        !operationIsCurrent(generation, owner, installationId)
      ) {
        return false;
      }
      const currentRegistration = result.registrations.find(
        (registration) => registration.installationId === installationId
      );
      const exactRegistration =
        currentRegistration &&
        currentRegistration.enabled &&
        currentRegistration.permissionState === "granted"
          ? currentRegistration
          : null;
      const serverActive = Boolean(exactRegistration);
      activeRef.current = serverActive;
      refreshRequiredRef.current = exactRegistration?.refreshRequired === true;
      providerTargetInvalidatedRef.current =
        currentRegistration?.providerTargetInvalidated === true;
      if (!providerTargetInvalidatedRef.current) {
        providerRecoveryPreparedRef.current = false;
      }
      if (serverActive) {
        setPushRegistrationCleanupIntent();
        await setPushRegistrationOwnerBinding(owner);
        if (
          controller.signal.aborted ||
          !operationIsCurrent(generation, owner, installationId)
        ) {
          return false;
        }
        if (getPermissionState() === "granted") {
          const localHealth = await inspectFirebaseRegistrationHealth();
          if (
            controller.signal.aborted ||
            !operationIsCurrent(generation, owner, installationId)
          ) {
            return false;
          }
          if (localHealth?.status !== "healthy") {
            activeRef.current = false;
            automaticRegistrationRef.current = {
              generation,
              owner,
              installationId,
              reason: "local_health_repair",
            };
            disposeObserver();
            setLifecycleState({
              status: "registration_in_progress",
              active: false,
              currentDeviceReference: exactRegistration.currentDeviceReference,
              registrationConfigurationMismatch: refreshRequiredRef.current,
            });
            return false;
          }
        }
        setLifecycleState({
          status: "registration_active",
          active: true,
          currentDeviceReference: exactRegistration.currentDeviceReference,
          registrationConfigurationMismatch: refreshRequiredRef.current,
        });
        if (getPermissionState() === "granted") {
          ensureObserver(generation, owner, installationId);
          if (refreshRequiredRef.current) {
            refreshRegistrationProvenance(generation, owner, installationId);
          }
        } else {
          disposeObserver();
        }
      } else {
        disposeObserver();
        setLifecycleState({
          status: config.vapidConfigured
            ? "registration_ready"
            : "registration_unavailable",
          active: false,
          currentDeviceReference: null,
          registrationConfigurationMismatch: false,
        });
      }
      return serverActive;
    } catch {
      if (
        !controller.signal.aborted &&
        operationIsCurrent(generation, owner, installationId)
      ) {
        setLifecycleState({
          status: "registration_lookup_failed",
          active: activeRef.current,
        });
      }
      return false;
    } finally {
      if (loadAbortRef.current === controller) loadAbortRef.current = null;
      releaseTrackedController(controller);
    }
  }, [
    config.enabled,
    config.vapidConfigured,
    createTrackedController,
    disposeObserver,
    ensureObserver,
    operationIsCurrent,
    refreshRegistrationProvenance,
    releaseTrackedController,
    setLifecycleState,
  ]);

  const registerCurrentDevice = useCallback(
    (options = {}) => {
      if (registrationPromiseRef.current) return registrationPromiseRef.current;
      const generation = generationRef.current;
      const owner = accountRef.current;
      const installationId = installationRef.current;
      const permissionGranted =
        getPermissionState() === "granted" || options.permissionGranted === true;
      if (
        !config.enabled ||
        !config.vapidConfigured ||
        !permissionGranted ||
        !operationIsCurrent(generation, owner, installationId)
      ) {
        return Promise.resolve(false);
      }

      if (options.consent !== undefined) {
        pendingConsentRef.current = {
          owner,
          installationId,
          consent: options.consent,
        };
      }
      const pendingConsent = pendingConsentRef.current;
      const consent =
        pendingConsent?.owner === owner &&
        pendingConsent?.installationId === installationId
          ? pendingConsent.consent
          : undefined;
      const attemptId = ++registrationAttemptSequenceRef.current;
      let resolveCompletion;
      const completion = new Promise((resolve) => {
        resolveCompletion = resolve;
      });
      registrationAttemptRef.current = {
        id: attemptId,
        generation,
        owner,
        installationId,
        resolve: resolveCompletion,
        timeoutId: null,
        claimController: null,
        providerPromise: null,
        cancelled: false,
        phase: "pre_provider",
        consent,
      };

      const timeoutId = setTimeout(() => {
        const attempt = registrationAttemptRef.current;
        if (attempt?.id !== attemptId) return;
        const timedOutBeforeProvider = attempt.phase === "pre_provider";
        attempt.cancelled = true;
        attempt.claimController?.abort();
        if (timedOutBeforeProvider) abandonPreProviderInitialization();
        else disposeObserver();
        finishRegistrationAttempt(false, attemptId);
        if (operationIsCurrent(generation, owner, installationId)) {
          setLifecycleState({ status: "registration_timeout", active: false });
        }
      }, REGISTRATION_WAIT_MS);
      registrationAttemptRef.current.timeoutId = timeoutId;

      setLifecycleState({ status: "registration_in_progress", active: false });
      const workflow = (async () => {
        if (
          providerTargetInvalidatedRef.current &&
          !providerRecoveryPreparedRef.current
        ) {
          disposeObserver();
          const resetResult =
            await resetFirebaseRegistrationForUnregisteredTarget();
          if (registrationAttemptRef.current?.id !== attemptId) return false;
          if (
            resetResult?.status !== "reset" ||
            !operationIsCurrent(generation, owner, installationId)
          ) {
            finishRegistrationAttempt(false, attemptId);
            if (operationIsCurrent(generation, owner, installationId)) {
              setLifecycleState({
                status: "registration_failed",
                active: false,
              });
            }
            return false;
          }
          providerRecoveryPreparedRef.current = true;
        }
        const session = await ensureObserver(generation, owner, installationId, {
          allowRegistration: true,
          permissionGranted: options.permissionGranted === true,
        });
        const attemptStillCurrent =
          registrationAttemptRef.current?.id === attemptId;
        if (
          session?.status !== "ready" ||
          !operationIsCurrent(generation, owner, installationId) ||
          !attemptStillCurrent
        ) {
          if (!attemptStillCurrent) return false;
          finishRegistrationAttempt(false, attemptId);
          if (operationIsCurrent(generation, owner, installationId)) {
            setLifecycleState({ status: "registration_failed", active: false });
          }
          return false;
        }
        const currentAttempt = registrationAttemptRef.current;
        if (currentAttempt?.id !== attemptId) return false;
        currentAttempt.phase = "provider";
        try {
          const providerRegistration = Promise.resolve().then(() =>
            session.register({
              permissionGranted: options.permissionGranted === true,
            })
          );
          providerRegistration.catch(() => undefined);
          if (registrationAttemptRef.current?.id === attemptId) {
            registrationAttemptRef.current.providerPromise =
              providerRegistration;
          }
          const outcome = await Promise.race([
            providerRegistration.then(
              () => ({ type: "provider_registered" }),
              () => ({ type: "provider_failed" })
            ),
            completion.then((successful) => ({
              type: "attempt_completed",
              successful,
            })),
          ]);
          if (outcome.type === "attempt_completed") {
            return outcome.successful;
          }
          if (outcome.type === "provider_failed") {
            throw new Error("push_registration_provider_failed");
          }
          return await completion;
        } catch {
          if (registrationAttemptRef.current?.id !== attemptId) return false;
          finishRegistrationAttempt(false, attemptId);
          disposeObserver();
          if (operationIsCurrent(generation, owner, installationId)) {
            setLifecycleState({ status: "registration_failed", active: false });
          }
          return false;
        }
      })().catch(() => {
        if (registrationAttemptRef.current?.id !== attemptId) return false;
        finishRegistrationAttempt(false, attemptId);
        disposeObserver();
        if (operationIsCurrent(generation, owner, installationId)) {
          setLifecycleState({ status: "registration_failed", active: false });
        }
        return false;
      });
      const operation = Promise.race([workflow, completion]).finally(() => {
        if (registrationPromiseRef.current === operation) {
          registrationPromiseRef.current = null;
        }
      });
      registrationPromiseRef.current = operation;
      return operation;
    },
    [
      config.enabled,
      config.vapidConfigured,
      abandonPreProviderInitialization,
      disposeObserver,
      ensureObserver,
      finishRegistrationAttempt,
      operationIsCurrent,
      setLifecycleState,
    ]
  );
  registerCurrentDeviceRef.current = registerCurrentDevice;

  const deactivateCurrentDevice = useCallback(() => {
    if (deactivationPromiseRef.current) return deactivationPromiseRef.current;
    const generation = generationRef.current;
    const owner = accountRef.current;
    const installationId = installationRef.current;
    if (!config.enabled || !operationIsCurrent(generation, owner, installationId)) {
      return Promise.resolve(false);
    }
    pendingConsentRef.current = null;

    setLifecycleState({ status: "deactivation_in_progress", active: true });
    const operation = enqueueMutation(async () => {
      if (!operationIsCurrent(generation, owner, installationId)) return false;
      const controller = createTrackedController();
      try {
        await revokePushRegistration(installationId, {
          signal: controller.signal,
        });
      } catch {
        if (
          !controller.signal.aborted &&
          operationIsCurrent(generation, owner, installationId)
        ) {
          setLifecycleState({ status: "deactivation_failed", active: true });
        }
        return false;
      } finally {
        releaseTrackedController(controller);
      }
      if (!operationIsCurrent(generation, owner, installationId)) return false;

      activeRef.current = false;
      disposeObserver();
      clearPushRegistrationOwnerBinding();
      const cleanupSucceeded = await cleanupLocalRegistration({ force: true });
      if (!operationIsCurrent(generation, owner, installationId)) return false;
      setLifecycleState({
        status: cleanupSucceeded
          ? config.vapidConfigured
            ? "registration_ready"
            : "registration_unavailable"
          : "deactivation_cleanup_failed",
        active: false,
      });
      return cleanupSucceeded;
    }).finally(() => {
      if (deactivationPromiseRef.current === operation) {
        deactivationPromiseRef.current = null;
      }
    });
    deactivationPromiseRef.current = operation;
    return operation;
  }, [
    cleanupLocalRegistration,
    config.enabled,
    config.vapidConfigured,
    createTrackedController,
    disposeObserver,
    enqueueMutation,
    operationIsCurrent,
    releaseTrackedController,
    setLifecycleState,
  ]);

  const prepareForLogout = useCallback(async () => {
    const owner = accountRef.current;
    const installationId = installationRef.current;
    const hasLocalRegistration =
      activeRef.current || hasPushRegistrationCleanupIntent();
    const ownerBindingStored =
      hasLocalRegistration && owner
        ? await setPushRegistrationOwnerBinding(owner)
        : false;
    const cleanupGeneration = invalidateGeneration();
    accountRef.current = null;
    setLifecycleState({
      status: "registration_feature_disabled",
      active: false,
    });

    let deliveryRevoked = false;
    if (config.enabled && owner && installationId) {
      const controller = createTrackedController();
      try {
        if (generationRef.current === cleanupGeneration) {
          await revokePushRegistration(installationId, {
            signal: controller.signal,
          });
          deliveryRevoked = true;
        }
      } catch {
        // Logout must continue after a current-device deactivation failure.
      } finally {
        releaseTrackedController(controller);
      }
    }
    if (hasLocalRegistration && (!deliveryRevoked || !ownerBindingStored)) {
      clearPushRegistrationOwnerBinding();
      await cleanupLocalRegistration({ force: true });
    }
  }, [
    cleanupLocalRegistration,
    config.enabled,
    createTrackedController,
    invalidateGeneration,
    releaseTrackedController,
    setLifecycleState,
  ]);

  const cleanupAfterAccountDeletion = useCallback(async () => {
    const lateProviderRegistration =
      registrationAttemptRef.current?.providerPromise || null;
    const shouldCleanLocally =
      activeRef.current ||
      hasPushRegistrationCleanupIntent() ||
      Boolean(
        registrationAttemptRef.current ||
          registrationPromiseRef.current ||
          observerRef.current ||
          observerPromiseRef.current
      );
    invalidateGeneration();
    accountRef.current = null;
    setLifecycleState({
      status: "registration_feature_disabled",
      active: false,
    });
    clearPushRegistrationCleanupIntent();
    if (shouldCleanLocally) {
      await cleanupLocalRegistration({ force: true });
      clearPushRegistrationCleanupIntent();
    }
    if (lateProviderRegistration) {
      lateProviderRegistration
        .catch(() => undefined)
        .then(() => cleanupLocalRegistration({ force: true }))
        .finally(() => clearPushRegistrationCleanupIntent());
    }
  }, [cleanupLocalRegistration, invalidateGeneration, setLifecycleState]);

  useImperativeHandle(
    lifecycleRef,
    () => ({ prepareForLogout, cleanupAfterAccountDeletion }),
    [cleanupAfterAccountDeletion, prepareForLogout]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidateGeneration();
    };
  }, [invalidateGeneration]);

  useEffect(() => {
    let cancelled = false;
    let started = false;
    const nextOwner = authResolved ? normalizeAccountKey(accountKey) : null;
    const timerId = setTimeout(async () => {
      if (cancelled) return;
      started = true;
      const previousOwner = accountRef.current;
      const switchedAccounts =
        Boolean(previousOwner) &&
        Boolean(nextOwner) &&
        previousOwner !== nextOwner;
      const generation = invalidateGeneration();
      accountRef.current = nextOwner;
      installationRef.current = null;
      setLifecycleState({
        status:
          config.enabled && nextOwner
            ? "registration_checking"
            : "registration_feature_disabled",
        active: false,
      });

      const hasRegistrationIntent = hasPushRegistrationCleanupIntent();
      const hasOwnerBinding = hasPushRegistrationOwnerBinding();
      const ownerBindingMatches =
        Boolean(nextOwner) && hasOwnerBinding
          ? await matchesPushRegistrationOwnerBinding(nextOwner)
          : false;
      let cleanupAttempted = false;
      if (
        hasRegistrationIntent &&
        (switchedAccounts ||
          (Boolean(nextOwner) && hasOwnerBinding && !ownerBindingMatches))
      ) {
        clearPushRegistrationOwnerBinding();
        await cleanupLocalRegistration({ force: true });
        cleanupAttempted = true;
      }
      if (cancelled || generation !== generationRef.current) return;

      if (!nextOwner) {
        if (
          authResolved &&
          hasPushRegistrationCleanupIntent() &&
          !hasPushRegistrationOwnerBinding()
        ) {
          await cleanupLocalRegistration({ force: true });
        }
        return;
      }
      if (!config.enabled) return;

      const installation = getOrCreatePushInstallation();
      if (cancelled || generation !== generationRef.current) return;
      installationRef.current = installation.installationId || null;
      if (!installationRef.current) {
        setLifecycleState({ status: "registration_unavailable", active: false });
        return;
      }
      const active = await reconcileCurrentDevice();
      if (cancelled || generation !== generationRef.current || active) return;

      const automaticRepair = automaticRegistrationRef.current;
      if (
        automaticRepair?.generation === generation &&
        automaticRepair.owner === nextOwner &&
        automaticRepair.installationId === installationRef.current
      ) {
        automaticRegistrationRef.current = null;
        await registerCurrentDeviceRef.current?.({ permissionGranted: true });
        return;
      }

      if (
        hasPushRegistrationCleanupIntent() &&
        hasPushRegistrationOwnerBinding() &&
        (await matchesPushRegistrationOwnerBinding(nextOwner)) &&
        getPermissionState() === "granted"
      ) {
        await registerCurrentDeviceRef.current?.({ permissionGranted: true });
      } else if (
        !cleanupAttempted &&
        hasPushRegistrationCleanupIntent() &&
        !hasPushRegistrationOwnerBinding()
      ) {
        await cleanupLocalRegistration({ force: true });
      }
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
      if (started) invalidateGeneration();
    };
  }, [
    accountKey,
    authResolved,
    cleanupLocalRegistration,
    config.enabled,
    invalidateGeneration,
    reconcileCurrentDevice,
    setLifecycleState,
  ]);

  const refreshPermissionLifecycle = useCallback(() => {
    const generation = generationRef.current;
    const owner = accountRef.current;
    const installationId = installationRef.current;
    if (!owner) {
      if (
        hasPushRegistrationCleanupIntent() &&
        !hasPushRegistrationOwnerBinding()
      ) {
        cleanupLocalRegistration();
      }
      return;
    }
    if (
      localCleanupRetryRef.current &&
      hasPushRegistrationCleanupIntent()
    ) {
      disposeObserver();
      cleanupLocalRegistration({ force: true }).then((successful) => {
        if (successful && activeRef.current) {
          setPushRegistrationCleanupIntent();
        }
      });
      return;
    }
    if (!activeRef.current || getPermissionState() !== "granted") {
      disposeObserver();
      return;
    }
    reconcileCurrentDevice().then((active) => {
      const repair = automaticRegistrationRef.current;
      if (
        !active &&
        repair?.generation === generation &&
        repair.owner === owner &&
        repair.installationId === installationId
      ) {
        automaticRegistrationRef.current = null;
        registerCurrentDeviceRef.current?.({ permissionGranted: true });
      }
    });
  }, [cleanupLocalRegistration, disposeObserver, reconcileCurrentDevice]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }
    const handleFocus = () => refreshPermissionLifecycle();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshPermissionLifecycle();
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    let permissionStatus = null;
    let cancelled = false;
    try {
      window.navigator?.permissions
        ?.query?.({ name: "notifications" })
        ?.then((status) => {
          if (cancelled) return;
          permissionStatus = status;
          permissionStatus?.addEventListener?.(
            "change",
            refreshPermissionLifecycle
          );
        })
        .catch(() => undefined);
    } catch {
      // Focus and visibility listeners remain the compatibility fallback.
    }

    return () => {
      cancelled = true;
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      permissionStatus?.removeEventListener?.(
        "change",
        refreshPermissionLifecycle
      );
    };
  }, [refreshPermissionLifecycle]);

  const value = useMemo(
    () => ({
      ...state,
      registrationEnabled: config.enabled,
      vapidConfigured: config.vapidConfigured,
      actions: {
        registerCurrentDevice,
        deactivateCurrentDevice,
        prepareForLogout,
        cleanupAfterAccountDeletion,
        refresh: reconcileCurrentDevice,
      },
    }),
    [
      cleanupAfterAccountDeletion,
      config.enabled,
      config.vapidConfigured,
      deactivateCurrentDevice,
      prepareForLogout,
      reconcileCurrentDevice,
      registerCurrentDevice,
      state,
    ]
  );

  return (
    <PushRegistrationLifecycleContext.Provider value={value}>
      {children}
    </PushRegistrationLifecycleContext.Provider>
  );
}
